/**
 * Five9 logger — the single choke point for events emitted by the Five9
 * adapter and its callbacks.
 *
 * Responsibilities:
 *   - Build canonical log entries with a stable schema (`SCHEMA_VERSION`).
 *   - Optionally redact PII via `sanitizeForLog` before persisting/forwarding
 *     (default allowlist is `'*'`, i.e. no redaction, per current requirements).
 *   - Batch bursts of entries within a single animation frame into one redux
 *     dispatch to keep steady-state re-render cost bounded.
 *   - Mirror every entry to `console.*` (dev visibility) and forward
 *     configured event types + `warn`/`error` to Application Insights.
 *
 * All `window.Five9`/SDK details stay outside this module; callers pass
 * plain-object payloads.
 *
 * @typedef {import('src/type-defs/five9Types').Five9LogEntry} Five9LogEntry
 * @typedef {import('src/type-defs/five9Types').Five9LogLevel} Five9LogLevel
 */
import { appInsightsService } from '@nsd/fe';
import { store } from 'src/store';
import { logAppend } from 'src/actions/five9Actions';
import { publishFive9Event } from 'src/services/five9EventsService';

/** @type {number} */
export const SCHEMA_VERSION = 1;

/** Event types forwarded to App Insights regardless of level. @type {Set<string>} */
const APP_INSIGHTS_EVENT_ALLOWLIST = new Set([
  // SDK lifecycle
  'sdk.timeout',
  'sdk.registered',
  'sdk.rehydrate',
  'sdk.rehydrate.unsupported',
  // crmApi-derived
  'crm.call.started',
  'crm.call.ended',
  // interactionApi lifecycle
  'interaction.subscribed',
  'interaction.ready',
  // interactionApi per-event stream (names verified from
  // `interactionApi.Events` — see `interaction.Events` diagnostic in the
  // log panel). Full stream stays local; only lifecycle-significant events
  // are forwarded to keep telemetry cost bounded.
  'interaction.callStarted',
  'interaction.callAccepted',
  'interaction.callRejected',
  'interaction.callEnded',
  'interaction.callFinished',
  'interaction.emailOffered',
  'interaction.emailAccepted',
  'interaction.emailRejected',
  'interaction.emailFinished',
  'interaction.chatOffered',
  'interaction.chatAccepted',
  'interaction.chatRejected',
  'interaction.chatFinished',
  // App-level
  'dispatch.associated'
]);

/** Numeric ranks used for `warn/error` App Insights forwarding threshold.
 *  @type {Record<Five9LogLevel, number>} */
const LEVELS = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40
};

/**
 * Redact/limit fields before persisting or forwarding to telemetry. Two
 * modes:
 *   - `allow: '*'` (default) — pass-through; used for in-app Redux/console
 *     visibility where the developer explicitly opted into full payloads.
 *   - `allow: [...names]` — allowlist only the named top-level fields; used
 *     by {@link buildTelemetryPayload} to redact PII (ANI/DNIS/raw SDK
 *     payloads) before forwarding to Application Insights.
 *
 * @template T
 * @param {T} payload
 * @param {{ allow?: '*' | string[] }} [opts]
 * @returns {T | Partial<T>}
 */
export function sanitizeForLog(payload, opts = {}) {
  const allow = opts.allow === undefined ? '*' : opts.allow;
  if (allow === '*' || payload == null || typeof payload !== 'object') {
    return payload;
  }
  const allowSet = new Set(allow);
  const out = {};
  Object.keys(payload).forEach((k) => {
    if (allowSet.has(k)) out[k] = payload[k];
  });
  return out;
}

/**
 * Fields safe to forward to Application Insights. Deliberately excludes
 * `ani`, `dnis`, `raw`, `args`, and anything else that could contain
 * customer phone numbers or verbatim SDK payloads. Keep this list tight —
 * telemetry cost is secondary to keeping PII off the wire to a shared
 * observability tenant.
 * @type {string[]}
 */
const SAFE_TELEMETRY_FIELDS = [
  'callSid',
  'state',
  'associatedDispatchId',
  'dispatchId',
  'count',
  'rawCount',
  'elapsedMs',
  'timeoutMs',
  'via',
  'reason',
  'message',
  'code',
  'level',
  'note',
  'interactionType',
  'eventName',
  'severityLevel',
  'unhandled',
  'status',
  'attempts'
];

/**
 * Build a redacted payload safe to send to Application Insights.
 *
 * @param {any} payload
 * @returns {any}
 */
const buildTelemetryPayload = (payload) => sanitizeForLog(payload, { allow: SAFE_TELEMETRY_FIELDS });

let idCounter = 0;
/** @returns {string} */
const nextId = () => {
  idCounter += 1;
  return `${Date.now().toString(36)}-${idCounter.toString(36)}`;
};

// --- Frame-batched dispatch --------------------------------------------------
/** @type {Five9LogEntry[]} */
let pendingEntries = [];
let flushScheduled = false;

/** @param {FrameRequestCallback} cb */
const raf = (cb) => (
  typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame(cb)
    : setTimeout(cb, 16)
);

const scheduleFlush = () => {
  if (flushScheduled) return;
  flushScheduled = true;
  raf(() => {
    flushScheduled = false;
    if (!pendingEntries.length) return;
    const batch = pendingEntries;
    pendingEntries = [];
    try {
      store.dispatch(logAppend(batch));
    } catch (err) {
      // Never let a logging failure break the app.
      // eslint-disable-next-line no-console
      console.error('[five9Logger] dispatch(logAppend) failed', err);
    }
  });
};

/**
 * @param {Five9LogLevel} level
 * @returns {(...args: any[]) => void}
 */
const consoleFor = (level) => {
  // eslint-disable-next-line no-console
  if (level === 'error') return console.error;
  // eslint-disable-next-line no-console
  if (level === 'warn') return console.warn;
  // eslint-disable-next-line no-console
  if (level === 'debug') return console.debug || console.log;
  // eslint-disable-next-line no-console
  return console.info || console.log;
};

const summarizePayload = (payload) => {
  if (!payload || typeof payload !== 'object') return { kind: typeof payload };
  const keys = Object.keys(payload);
  return {
    kind: Array.isArray(payload) ? 'array' : 'object',
    keyCount: keys.length,
    keys: keys.slice(0, 20),
  };
};

/** @param {Five9LogEntry} entry */
const forwardToAppInsights = (entry) => {
  try {
    const shouldForward = LEVELS[entry.level] >= LEVELS.warn
      || APP_INSIGHTS_EVENT_ALLOWLIST.has(entry.type);
    if (!shouldForward) return;
    const ai = appInsightsService?.appInsights;
    if (!ai) return;
    // Strip PII/raw SDK payloads before crossing the App Insights
    // trust boundary. Full payloads remain available in-app via the
    // Redux `log` slice + `Five9LogPanel` for developer diagnostics.
    const safePayload = buildTelemetryPayload(entry.payload);
    if (LEVELS[entry.level] >= LEVELS.warn && typeof ai.trackTrace === 'function') {
      ai.trackTrace({
        message: `[five9] ${entry.type}: ${entry.message || ''}`,
        severityLevel: entry.level === 'error' ? 3 : 2,
        properties: {
          five9Type: entry.type,
          payloadSummary: summarizePayload(entry.payload),
          payloadRedacted: true,
          payload: safePayload
        }
      });
    }
    if (APP_INSIGHTS_EVENT_ALLOWLIST.has(entry.type) && typeof ai.trackEvent === 'function') {
      ai.trackEvent(
        { name: `DispatchQoreFE::Five9::${entry.type}` },
        {
          payloadSummary: summarizePayload(entry.payload),
          payloadRedacted: true,
          payload: safePayload
        }
      );
    }
  } catch (_) {
    // swallow — telemetry failures must not affect UX
  }
};

const emit = (level, type, payload, message) => {
  /** @type {Five9LogEntry} */
  const entry = {
    id: nextId(),
    ts: Date.now(),
    level,
    type,
    message: message || '',
    payload: sanitizeForLog(payload)
  };
  try {
    consoleFor(level)(`[five9:${type}]`, message || '', {
      payloadRedacted: true,
      payloadSummary: summarizePayload(payload),
      payload: buildTelemetryPayload(payload)
    });
  } catch (_) { /* noop */ }
  forwardToAppInsights(entry);
  try {
    const state = store.getState();
    const user = state?.account?.user || null;
    const activeCallSid = state?.five9?.activeCallSid || null;
    const activeCall = activeCallSid ? state?.five9?.calls?.[activeCallSid] : null;
    publishFive9Event(entry, {
      user,
      activeDispatchId: activeCall?.associatedDispatchId || null,
      screenpopCore: activeCall?.screenpopCore || null,
    });
  } catch (_) {
    // swallow — event export failures must not affect UX
  }
  pendingEntries.push(entry);
  scheduleFlush();
  return entry;
};

/**
 * Public logger surface. Every method builds a canonical {@link Five9LogEntry},
 * batches it into a single per-frame redux dispatch, mirrors it to
 * `console.*`, and (for allowlisted event types or `warn`/`error` levels)
 * forwards it to Application Insights.
 *
 * @type {{
 *   SCHEMA_VERSION: number,
 *   debug: (type: string, payload?: any, message?: string) => Five9LogEntry,
 *   info:  (type: string, payload?: any, message?: string) => Five9LogEntry,
 *   warn:  (type: string, payload?: any, message?: string) => Five9LogEntry,
 *   error: (type: string, payload?: any, message?: string) => Five9LogEntry,
 *   event: (type: string, payload?: any, message?: string) => Five9LogEntry,
 * }}
 */
const logger = {
  SCHEMA_VERSION,
  debug: (type, payload, message) => emit('debug', type, payload, message),
  info: (type, payload, message) => emit('info', type, payload, message),
  warn: (type, payload, message) => emit('warn', type, payload, message),
  error: (type, payload, message) => emit('error', type, payload, message),
  /** Semantic alias for callback/lifecycle events (info-level). */
  event: (type, payload, message) => emit('info', type, payload, message)
};

export default logger;
